import React, { useEffect, useRef, useState } from 'react';
import {
  IEmail,
  IFormato,
  IFormContacto,
  IFormDatosContactoExt,
  IFormulario,
  IMenuItem,
} from 'utils/models/interfaces';
import PageContainer from 'components/PageContainer/PageContainer';
import Formulario from 'components/public/Formulario/Formulario';
import { datosFormContactoExt, datosFormDatosPersonales, datosFormMensaje, tooltips } from 'utils/formData';
import { useError } from 'services/ErrorContext';
import { anchoMaxFormContacto, datosTeatroIconos, googleMapsTeatro } from 'utils/constants';
import { solicitudFormularios } from 'services/servicioPost';
import { ForwardToInboxOutlined } from '@mui/icons-material';
import { Box, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { theme } from 'theme/theme';
import { getAuthToken } from 'services/apiUtils';

const Contacto = () => {
  const { error, clearError, loading, setError, setLoading } = useError();

  const [mensajeExito, setMensajeExito] = useState('');
  const usuarioJSON = localStorage.getItem('usuario');
  const usuario = usuarioJSON && JSON.parse(usuarioJSON);
  const [altura, setAltura] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);
  const token = getAuthToken();

  const datosNombreApellido = datosFormDatosPersonales.filter(
    dato => dato.name === 'nombre' || dato.name === 'apellido'
  );

  useEffect(() => {
    if (formRef.current) {
      const alturaElemento = formRef.current.offsetHeight;
      setAltura(alturaElemento - 140);
    }
  }, []);

  const datosFormContactoLogueado: IFormato<IFormDatosContactoExt>[] = [
    {
      name: 'Telefono',
      label: 'Teléfono',
      inputType: 'tel',
      halfWidth: 'left',
      pattern: tooltips.telefono.pattern,
      tooltip: tooltips.telefono.tooltip,
      adornment: 'tel',
      defaultValue: usuario?.Telefono,
      subtituloSeccion: 'Ingresá los datos para que podamos contactarnos contigo',
    },
    {
      name: 'Email',
      label: 'Email',
      required: false,
      inputType: 'email',
      adornment: 'email',
      pattern: tooltips.email.pattern,
      tooltip: tooltips.email.tooltip,
      halfWidth: 'right',
      defaultValue: usuario?.Email,
    },
  ];

  const datosFormContacto: IFormato<IFormContacto>[] = usuario
    ? [...datosFormContactoLogueado, ...datosFormMensaje]
    : [...datosNombreApellido, ...datosFormContactoExt, ...datosFormMensaje];

  async function handleSubmit(data: IFormulario): Promise<void> {
    clearError();

    const nombre = data.nombre || usuario?.Nombre;
    const apellido = data.apellido || usuario?.Apellido;
    const email = data.Email || usuario?.Email;
    const telefono = data.Telefono || usuario?.Telefono;
    const mensaje = data.mensaje;

    const request: IEmail = {
      Subject: 'Teatro Bosch Web - Nueva solicitud de contacto',
      Body: `
        Nombre: ${nombre} <br>
        Apellido: ${apellido}<br>
        Email: ${email ? email : 'No ingresado'} <br>
        Teléfono: ${telefono}<br>
        Mensaje: ${mensaje}
        `,
      Title: `Motivo de consulta: ${data.motivoConsulta ? data.motivoConsulta : 'Contacto'}`,
    };

    try {
      if (!nombre || !apellido || !telefono || !mensaje) {
        setError('Error en alguno de los datos');
        throw new Error('Error en alguno de los datos');
      }

      await solicitudFormularios('contacto', request, setError, setLoading);
      setMensajeExito(`¡Gracias por comunicarte con nosotros! Nos pondremos en contacto contigo.`);
    } catch (err) {
    }
  }

  const handleVolver = () => {
    setMensajeExito('');
  };

  return (
    <PageContainer marginBottom={true}>
      <Box
        display={{ xs: 'block', lg: 'flex' }}
        gap="40px"
        alignItems="start"
        minHeight={{ xs: '100%', md: altura }}
        maxWidth={anchoMaxFormContacto}
        mx="auto"
      >
        {/* Nuestros datos */}
        <Box
          textAlign="left"
          padding="30px 25px 64px 25px"
          borderRadius={5}
          flex="1"
          height={{ xs: '100%', lg: altura }}
          maxWidth="600px"
          mx="auto"
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.06)', flex: '1' }}
        >
          <Typography variant="h4" fontSize={{ xs: '30px', md: '40px' }} fontWeight={600} pb="46px">
            Nuestros datos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box mt={token ? '' : 7} sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {datosTeatroIconos.map(({ path, icon: Icon, index }: IMenuItem) => (
              <Box display="flex" alignItems="center" gap={2} key={`${index}-${path}`}>
                {Icon && <Icon sx={{ color: theme.palette.primary.main }} />}
                <Typography py={1} fontWeight={!Icon ? 600 : 400} fontSize={!Icon ? '18px' : 'unset'}>
                  {path}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Formulario Contacto */}
        <Box ref={formRef} m={0} p={0} sx={{ flex: '1' }}>
          <Formulario
            campos={datosFormContacto}
            titulo="Contacto"
            onSubmit={handleSubmit}
            onVolver={handleVolver}
            mensajeExito={mensajeExito}
            mensajeError={error}
            loading={loading}
            iconoBoton={ForwardToInboxOutlined}
          />
        </Box>
      </Box>

      {/* Google Maps */}
      <Box marginX="auto" sx={{ width: anchoMaxFormContacto }}>
        {!mensajeExito && (
          <iframe
            title="Ubicación del Teatro Bosch en Google Maps"
            src={googleMapsTeatro}
            width="100%"
            height="450"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 'none', margin: '50px 0 50px 0' }}
          ></iframe>
        )}
      </Box>
    </PageContainer>
  );
};

export default Contacto;
